import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Chip, ChipGroup } from "../../ui";
import s from "../clinic.module.css";
import aa from "./adminApp.module.css";

function HubChevronBack() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M14.5 6.5L9 12l5.5 5.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** iOS-style nav bar: back + title (+ optional right action). */
export function HubNav({
  title,
  onBack,
  right,
  backLabel = "Назад",
  showBack = true,
}) {
  const navigate = useNavigate();
  const handleBack = onBack ?? (() => navigate("/manage/app/more"));

  return (
    <header className={aa.hubNav}>
      <div className={aa.hubNavBar}>
        {showBack ? (
          <button
            type="button"
            className={aa.hubNavBack}
            onClick={handleBack}
            aria-label={backLabel}
          >
            <HubChevronBack />
          </button>
        ) : (
          <span className={aa.hubNavSideSpacer} aria-hidden />
        )}
        <h1 className={aa.hubNavTitle}>{title}</h1>
        <div className={aa.hubNavRight}>{right ?? <span className={aa.hubNavSideSpacer} aria-hidden />}</div>
      </div>
    </header>
  );
}

export function HubNavAdd({ onClick, ariaLabel = "Добавить" }) {
  return (
    <button
      type="button"
      className={aa.hubNavAddBtn}
      onClick={onClick}
      aria-label={ariaLabel}
      title={ariaLabel}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M12 5v14M5 12h14"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    </button>
  );
}

/** PWA hub page shell: sticky nav + padded body. */
export function HubScreen({
  title,
  onBack,
  right,
  className = "",
  bodyClassName = "",
  children,
}) {
  return (
    <div className={`${aa.hubPage} ${aa.hubPageBleed} ${className}`.trim()}>
      <HubNav title={title} onBack={onBack} right={right} />
      <div className={`${aa.hubPageBody} ${aa.hubPageBodyAnimated} ${bodyClassName}`.trim()}>{children}</div>
    </div>
  );
}

/** Корневая вкладка PWA (Клиенты, Календарь, Жизнь) — шапка как в настройках, без «Назад». */
export function HubTabScreen({
  title,
  right,
  className = "",
  bodyClassName = "",
  children,
}) {
  return (
    <div className={`${aa.hubPage} ${aa.hubPageBleed} ${className}`.trim()}>
      <HubNav title={title} showBack={false} right={right} />
      <div className={`${aa.hubPageBody} ${aa.hubPageBodyAnimated} ${bodyClassName}`.trim()}>{children}</div>
    </div>
  );
}

export function HubToolbar({ countLabel, onAdd, addAriaLabel = "Добавить" }) {
  return (
    <div
      className={`${aa.hubActionBar} ${
        countLabel && onAdd ? aa.hubActionBarStart : ""
      }`}
    >
      {countLabel ? <span className={aa.hubCount}>{countLabel}</span> : null}
      {onAdd ? (
        <button
          type="button"
          className={aa.clientsAddBtn}
          onClick={onAdd}
          aria-label={addAriaLabel}
          title={addAriaLabel}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M12 5v14M5 12h14"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      ) : null}
    </div>
  );
}

/** Detail screen: back to list (not «Ещё»). */
export function HubBack({ title, onBack }) {
  return <HubNav title={title} onBack={onBack} backLabel="К списку" />;
}

export function HubListGroup({ children }) {
  return <div className={aa.hubCardList}>{children}</div>;
}

export function HubRow({
  title,
  meta,
  sub,
  active,
  onClick,
  badge,
  badgeTone,
  progress,
}) {
  const badgeClass =
    badgeTone === "warn"
      ? aa.hubRowBadgeWarn
      : badgeTone === "danger"
        ? aa.hubRowBadgeDanger
        : badgeTone === "ok"
          ? aa.hubRowBadgeOk
          : badgeTone === "accent"
            ? aa.hubRowBadgeAccent
            : aa.hubRowBadge;

  const interactive = typeof onClick === "function";

  return (
    <article className={`${aa.hubRow} ${active ? aa.hubRowActive : ""}`.trim()}>
      <button
        type="button"
        className={`${aa.hubRowBtn} ${interactive ? "" : aa.hubRowBtnStatic}`.trim()}
        onClick={interactive ? onClick : undefined}
        disabled={!interactive}
      >
        <span className={aa.hubRowBody}>
          <span className={aa.hubRowTop}>
            <strong className={aa.hubRowName}>{title}</strong>
            {badge ? <span className={badgeClass}>{badge}</span> : null}
          </span>
          {meta ? <span className={aa.hubRowMeta}>{meta}</span> : null}
          {sub ? <span className={aa.hubRowSub}>{sub}</span> : null}
          {progress != null ? (
            <span className={aa.hubProgress} aria-hidden>
              <span className={aa.hubProgressFill} style={{ width: `${progress}%` }} />
            </span>
          ) : null}
        </span>
        {interactive ? <span className={aa.hubRowChevron} aria-hidden /> : null}
      </button>
    </article>
  );
}

export function HubKpiStrip({ items }) {
  if (!items?.length) return null;
  return (
    <div className={aa.hubKpiStrip}>
      {items.map((item) => (
        <button
          key={item.key}
          type="button"
          className={
            item.active ? `${aa.hubKpiCell} ${aa.hubKpiCellActive}` : aa.hubKpiCell
          }
          onClick={item.onClick}
          disabled={!item.onClick}
        >
          <span
            className={item.warn ? `${aa.hubKpiVal} ${aa.hubKpiValWarn}` : aa.hubKpiVal}
          >
            {item.value}
          </span>
          <span className={aa.hubKpiLbl}>{item.label}</span>
        </button>
      ))}
    </div>
  );
}

export function HubSegment({ tabs, active, onChange, className = "" }) {
  if (!tabs?.length) return null;

  const scrollable = tabs.length >= 3;

  return (
    <div className={`${aa.hubSegmentWrap} ${className}`.trim()}>
      <div
        className={[
          aa.hubSegment,
          scrollable ? aa.hubSegmentScroll : "",
        ]
          .filter(Boolean)
          .join(" ")}
        role="tablist"
        aria-label="Вкладки"
      >
        {tabs.map((t) => {
          const selected = active === t.id;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={selected}
              className={selected ? aa.hubSegmentBtnActive : aa.hubSegmentBtn}
              onClick={() => onChange(t.id)}
            >
              {t.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Компактные чипы-фильтры (не как основные вкладки HubSegment). */
export function HubFilterChips({
  tabs,
  active,
  onChange,
  className = "",
  scroll,
}) {
  if (!tabs?.length) return null;

  const useScroll = scroll ?? tabs.length > 6;
  const chips = (
    <ChipGroup label="Фильтр" className={className}>
      {tabs.map((t) => (
        <Chip
          key={t.id}
          selected={String(active) === String(t.id)}
          count={t.count}
          onClick={() => onChange(t.id)}
        >
          {t.label}
        </Chip>
      ))}
    </ChipGroup>
  );

  if (useScroll) {
    return <div className={aa.hubChipScroll}>{chips}</div>;
  }
  return chips;
}

const SHEET_MS = 260;
const SHEET_EASE = "cubic-bezier(0.32, 0.72, 0, 1)";
const SHEET_CLOSE_EASE = "cubic-bezier(0.4, 0, 0.6, 1)";
const DRAG_MIN_CLOSE = 56;
const DRAG_CLOSE_RATIO = 0.18;
const FLING_VELOCITY = 0.35;

/** Нижняя шторка (портал в body — не обрезается контейнером страницы). */
export function HubSheet({
  open,
  onClose,
  title,
  subtitle,
  titleId,
  header,
  children,
  footer,
  sheetClassName = "",
  bodyClassName = "",
}) {
  const [visible, setVisible] = useState(false);
  const [offsetY, setOffsetY] = useState(0);
  const [withTransition, setWithTransition] = useState(false);
  const [dragging, setDragging] = useState(false);

  const panelRef = useRef(null);
  const offsetRef = useRef(0);
  const closingRef = useRef(false);
  const dragRef = useRef(null);
  const closeTimerRef = useRef(null);

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const panelHeight = () =>
    panelRef.current?.offsetHeight || Math.round(window.innerHeight * 0.9);

  const runClose = useCallback(
    (notifyParent, fromOffset) => {
      if (closingRef.current) return;
      closingRef.current = true;
      clearCloseTimer();

      const start = Math.max(0, fromOffset ?? offsetRef.current);
      offsetRef.current = start;
      setOffsetY(start);
      setWithTransition(true);
      setDragging(false);
      dragRef.current = null;

      requestAnimationFrame(() => {
        const h = panelHeight();
        offsetRef.current = h;
        setOffsetY(h);
      });

      closeTimerRef.current = setTimeout(() => {
        closingRef.current = false;
        setVisible(false);
        setWithTransition(false);
        offsetRef.current = 0;
        setOffsetY(0);
        dragRef.current = null;
        if (notifyParent) onClose?.();
      }, SHEET_MS);
    },
    [onClose],
  );

  useEffect(() => {
    if (!open) return undefined;

    closingRef.current = false;
    clearCloseTimer();
    setVisible(true);
    setDragging(false);
    setWithTransition(false);
    const startY = window.innerHeight;
    offsetRef.current = startY;
    setOffsetY(startY);

    const enterId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setWithTransition(true);
        offsetRef.current = 0;
        setOffsetY(0);
      });
    });

    return () => cancelAnimationFrame(enterId);
  }, [open]);

  useEffect(() => {
    if (open || !visible || closingRef.current) return;
    runClose(false);
  }, [open, visible, runClose]);

  useEffect(() => {
    if (!visible) return undefined;
    document.documentElement.classList.add("hub-sheet-open");
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.documentElement.classList.remove("hub-sheet-open");
      document.body.style.overflow = prev;
    };
  }, [visible]);

  useEffect(() => () => clearCloseTimer(), []);

  const dismiss = useCallback(() => {
    runClose(true);
  }, [runClose]);

  const onDragStart = (e) => {
    if (closingRef.current || e.button !== 0) return;
    if (e.target.closest("button, input, select, textarea, a, label")) return;

    dragRef.current = {
      pointerId: e.pointerId,
      startY: e.clientY,
      startOffset: offsetRef.current,
      lastY: e.clientY,
      lastT: performance.now(),
      velocity: 0,
    };
    setDragging(true);
    setWithTransition(false);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onDragMove = (e) => {
    const drag = dragRef.current;
    if (!drag || e.pointerId !== drag.pointerId) return;

    const dy = e.clientY - drag.startY;
    const next = Math.max(0, drag.startOffset + dy);
    const now = performance.now();
    const dt = now - drag.lastT;
    if (dt > 0) {
      drag.velocity = (e.clientY - drag.lastY) / dt;
    }
    drag.lastY = e.clientY;
    drag.lastT = now;
    offsetRef.current = next;
    setOffsetY(next);

    if (next > 0 && e.cancelable) e.preventDefault();
  };

  const onDragEnd = (e) => {
    const drag = dragRef.current;
    if (!drag || e.pointerId !== drag.pointerId) return;

    e.currentTarget.releasePointerCapture(e.pointerId);
    dragRef.current = null;
    setDragging(false);

    const offset = offsetRef.current;
    const h = panelHeight();
    const threshold = Math.max(DRAG_MIN_CLOSE, h * DRAG_CLOSE_RATIO);
    const flingDown = drag.velocity > FLING_VELOCITY;

    if (offset >= threshold || flingDown) {
      runClose(true, offset);
      return;
    }

    setWithTransition(true);
    offsetRef.current = 0;
    setOffsetY(0);
  };

  if (!visible) return null;

  const id = titleId || "hub-sheet-title";
  const h = panelHeight();
  const backdropAlpha = Math.max(0.12, 0.48 * (1 - Math.min(offsetY, h) / h));

  return createPortal(
    <div
      className={aa.hubSheetBackdrop}
      style={{
        backgroundColor: `rgba(15, 23, 42, ${backdropAlpha})`,
        transition: withTransition && !dragging
          ? `background-color ${SHEET_MS}ms ease`
          : "none",
      }}
      onClick={() => dismiss()}
      role="presentation"
    >
      <div
        ref={panelRef}
        className={`${aa.hubSheet} ${
          dragging || offsetY > 0 ? aa.hubSheetDragging : ""
        } ${sheetClassName}`.trim()}
        style={{
          transform: `translate3d(0, ${offsetY}px, 0)`,
          transition:
            withTransition && !dragging
              ? `transform ${SHEET_MS}ms ${closingRef.current ? SHEET_CLOSE_EASE : SHEET_EASE}`
              : "none",
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={id}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={aa.hubSheetDragHeader}
          onPointerDown={onDragStart}
          onPointerMove={onDragMove}
          onPointerUp={onDragEnd}
          onPointerCancel={onDragEnd}
        >
          <div className={aa.hubSheetDragZone}>
            <div className={aa.hubSheetHandle} aria-hidden />
          </div>
          {header ?? (
            <header className={aa.hubSheetHead}>
              <div className={aa.hubSheetHeadText}>
                <h3 id={id}>{title}</h3>
                {subtitle ? (
                  <p className={aa.hubSheetSubtitle}>{subtitle}</p>
                ) : null}
              </div>
              <button
                type="button"
                className={aa.hubSheetCloseBtn}
                onClick={() => dismiss()}
                aria-label="Закрыть"
              >
                ×
              </button>
            </header>
          )}
        </div>
        <div className={`${aa.hubSheetBody} ${bodyClassName}`.trim()}>
          {children}
        </div>
        {footer ? <footer className={aa.hubSheetFoot}>{footer}</footer> : null}
      </div>
    </div>,
    document.body,
  );
}

export function HubFormCard({ title, children }) {
  return (
    <section className={aa.hubFormCard}>
      {title ? <h3 className={aa.hubFormCardTitle}>{title}</h3> : null}
      {children}
    </section>
  );
}

export function HubDetailPanel({ children }) {
  return <div className={aa.hubDetailPanel}>{children}</div>;
}

export function HubSectionTitle({ children }) {
  return <h3 className={aa.hubSectionLabel}>{children}</h3>;
}

/** Кнопки внизу шторки: основное действие + «Отмена». */
export function HubSheetActions({
  primary,
  secondaryLabel = "Отмена",
  onSecondary,
  primaryForm,
  primaryType = "submit",
  primaryDisabled = false,
  onPrimary,
}) {
  return (
    <>
      <button
        type={primaryType}
        form={primaryForm}
        disabled={primaryDisabled}
        className={aa.hubSheetBtnPrimary}
        onClick={onPrimary}
      >
        {primary}
      </button>
      <button
        type="button"
        className={aa.hubSheetBtnGhost}
        onClick={onSecondary}
      >
        {secondaryLabel}
      </button>
    </>
  );
}
